import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { C, audit, WA } from '../constants';
import { Btn, Input, Card, Toast, NivelBadge } from '../components/Atoms';
import { NavbarApp } from './MisTandas';

// ─── BIOMÉTRICO TOGGLE ─────────────────────────────────────
function BiometricoToggle({ usuario, showToast }) {
  const [activo, setActivo] = useState(false);
  useEffect(() => { try { setActivo(!!localStorage.getItem('tmx_bio_cred')); } catch(e) {} }, []); // eslint-disable-line
  const [cargando, setCargando] = useState(false);

  const activar = async () => {
    if (!WA.disponible()) {
      showToast('Tu dispositivo no soporta Face ID / huella', 'error'); return;
    }
    // Validar que tenemos los datos del usuario antes de llamar WebAuthn
    const uid   = usuario?.id   || '';
    const email = usuario?.email || usuario?.nombre || 'usuario@tandaclick.com';
    if (!uid) { showToast('Error: sesión no válida, vuelve a iniciar sesión', 'error'); return; }
    setCargando(true);
    try {
      const cred = await WA.registrar(uid, email);
      // Guardar en Supabase
      const { error } = await supabase.from('webauthn_credentials').upsert({
        usuario_id: uid,
        credential_id: cred.credentialId,
        public_key: cred.publicKey,
        device_name: navigator.userAgent.includes('iPhone') ? 'iPhone' :
                     navigator.userAgent.includes('Android') ? 'Android' : 'Este dispositivo',
        counter: 0,
      }, { onConflict: 'credential_id' });
      if (error) throw error;
      // Guardar en localStorage para acceso rápido
      try { localStorage.setItem('tmx_bio_cred', cred.credentialId); } catch(e) {}
      try { localStorage.setItem('tmx_bio_email', email); } catch(e) {}
      await audit('biometrico_activado', { device: navigator.userAgent.slice(0, 60) });
      setActivo(true);
      showToast('¡Face ID / Huella activado! Ya puedes entrar sin contraseña 🔐', 'success');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        showToast('Activación cancelada', 'info');
      } else if (err.name === 'InvalidStateError') {
        showToast('Este dispositivo ya tiene Face ID activado', 'info');
        setActivo(true);
      } else {
        showToast('Error al activar: ' + err.message, 'error');
      }
    }
    setCargando(false);
  };

  const desactivar = async () => {
    const credId = localStorage.getItem('tmx_bio_cred');
    if (credId) await supabase.from('webauthn_credentials').delete().eq('credential_id', credId);
    try { localStorage.removeItem('tmx_bio_cred'); localStorage.removeItem('tmx_bio_email'); localStorage.removeItem('tmx_session_token'); } catch(e) {}
    await audit('biometrico_desactivado', {});
    setActivo(false);
    showToast('Acceso biométrico desactivado', 'info');
  };

  const bioSoportado = WA.disponible();

  if (!bioSoportado) return (
    <div style={{ fontSize: 13, color: C.textLt, padding: '8px 0' }}>
      Tu dispositivo no soporta Face ID / huella digital.
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 24 }}>{activo ? '🔐' : '🔓'}</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>
                {activo ? 'Face ID / Huella activado' : 'Face ID / Huella desactivado'}
              </div>
              <div style={{ fontSize: 12, color: C.textLt, marginTop: 1 }}>
                {activo ? 'Entras directo sin contraseña' : 'Actívalo para entrar más rápido'}
              </div>
            </div>
          </div>
        </div>
        <button
          onClick={activo ? desactivar : activar}
          disabled={cargando}
          style={{
            padding: '10px 18px', borderRadius: 10, border: 'none', cursor: cargando ? 'not-allowed' : 'pointer',
            background: activo ? C.dangerLt : C.successLt,
            color: activo ? C.danger : C.success,
            fontSize: 13, fontWeight: 800,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}>
          {cargando ? '⏳' : activo ? 'Desactivar' : 'Activar'}
        </button>
      </div>
      {activo && (
        <div style={{ background: C.successLt, borderRadius: 10, padding: '10px 12px', fontSize: 12, color: C.success, lineHeight: 1.6 }}>
          ✓ Al abrir la app se te pedirá Face ID o huella — sin necesidad de escribir tu contraseña.
        </div>
      )}
    </div>
  );
}

// ─── MI PERFIL ─────────────────────────────────────────────
function MiPerfil({ usuario, setUsuario, onMenu, onHome }) {
  const [toast, setToast] = useState({ msg: '', type: '' });
  const showToast = (msg, type = 'info') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: '' }), 4000); };
  const [copiadoCliente, setCopiadoCliente] = useState(false);
  const [editandoMetodo, setEditandoMetodo] = useState(false);
  const [nuevoPago, setNuevoPago] = useState(usuario.metodo_pago || 'spei');
  const [nuevoCobro, setNuevoCobro] = useState(usuario.metodo_cobro || 'spei');
  const [nuevaClabe, setNuevaClabe] = useState(usuario.clabe || '');
  const [nuevoTitular, setNuevoTitular] = useState(usuario.nombre_titular || '');
  const [nuevoBanco, setNuevoBanco] = useState(usuario.banco || '');
  const [guardandoMetodo, setGuardandoMetodo] = useState(false);
  const [editandoInfo, setEditandoInfo] = useState(false);
  const [infoCalle, setInfoCalle] = useState(usuario.direccion_calle || '');
  const [infoNoExt, setInfoNoExt] = useState(usuario.no_exterior || '');
  const [infoNoInt, setInfoNoInt] = useState(usuario.no_interior || '');
  const [infoColonia, setInfoColonia] = useState(usuario.colonia || '');
  const [infoCiudad, setInfoCiudad] = useState(usuario.ciudad || '');
  const [infoEstado, setInfoEstado] = useState(usuario.estado || '');
  const [infoCP, setInfoCP] = useState(usuario.codigo_postal || '');
  const [coloniaOpciones, setColoniaOpciones] = useState([]);
  const [buscandoCP, setBuscandoCP] = useState(false);
  const [guardandoInfo, setGuardandoInfo] = useState(false);
  const [verificandoIdentidad, setVerificandoIdentidad] = useState(false);

  const buscarCP = async () => {
    if (infoCP.length !== 5) return;
    setBuscandoCP(true);
    try {
      // TODO: usar token real de copomex en producción
      const res = await fetch(`https://api.copomex.com/query/info_cp/${infoCP}?token=demoapitoken`);
      const data = await res.json();
      if (data && data.error === false && data.response) {
        const r = Array.isArray(data.response) ? data.response[0] : data.response;
        setInfoEstado(r.estado || '');
        setInfoCiudad(r.municipio || r.ciudad || '');
        const colonias = Array.isArray(data.response)
          ? data.response.map(d => d.asentamiento).filter(Boolean)
          : [r.asentamiento].filter(Boolean);
        setColoniaOpciones(colonias);
        if (colonias.length === 1) setInfoColonia(colonias[0]);
      } else {
        // Fallback: API de sepomex pública
        const res2 = await fetch(`https://sepomex.icalialabs.com/api/v1/zip_codes?zip_code=${infoCP}`);
        const data2 = await res2.json();
        if (data2.zip_codes && data2.zip_codes.length > 0) {
          const z = data2.zip_codes[0];
          setInfoEstado(z.d_estado || '');
          setInfoCiudad(z.d_mnpio || '');
          const cols = data2.zip_codes.map(z => z.d_asenta).filter(Boolean);
          setColoniaOpciones(cols);
          if (cols.length === 1) setInfoColonia(cols[0]);
        } else {
          showToast('Código postal no encontrado', 'error');
        }
      }
    } catch(e) {
      showToast('No se pudo buscar el CP. Llena los campos manualmente.', 'info');
    }
    setBuscandoCP(false);
  };

  const guardarMetodo = async () => {
    if (nuevoCobro === 'spei') {
      if (!nuevaClabe || !/^\d{18}$/.test(nuevaClabe)) { showToast('Ingresa una CLABE válida de 18 dígitos', 'error'); return; }
      // Validate CLABE checksum
      const pesos = [3,7,1,3,7,1,3,7,1,3,7,1,3,7,1,3,7];
      let suma = 0;
      for (let i = 0; i < 17; i++) suma += (parseInt(nuevaClabe[i]) * pesos[i]) % 10;
      const control = (10 - (suma % 10)) % 10;
      if (control !== parseInt(nuevaClabe[17])) { showToast('CLABE inválida — verifica el número', 'error'); return; }
      if (!nuevoTitular.trim()) { showToast('Ingresa el nombre del titular', 'error'); return; }
      if (!nuevoBanco.trim()) { showToast('Ingresa el nombre del banco', 'error'); return; }
    }
    setGuardandoMetodo(true);
    const { error } = await supabase.from('usuarios').update({
      metodo_pago: nuevoPago,
      metodo_cobro: nuevoCobro,
      clabe: nuevoCobro === 'spei' ? nuevaClabe : null,
      nombre_titular: nuevoCobro === 'spei' ? nuevoTitular : null,
      banco: nuevoCobro === 'spei' ? nuevoBanco : null,
    }).eq('id', usuario.id);
    setGuardandoMetodo(false);
    if (error) { showToast('Error al guardar', 'error'); return; }
    setUsuario(prev => ({ ...prev, metodo_pago: nuevoPago, metodo_cobro: nuevoCobro, clabe: nuevaClabe, nombre_titular: nuevoTitular, banco: nuevoBanco }));
    setEditandoMetodo(false);
    showToast('Método de pago actualizado ✓', 'success');
  };

  const guardarInfo = async () => {
    if (!infoCalle || !infoNoExt || !infoColonia || !infoCiudad || !infoEstado || !infoCP) {
      showToast('Completa todos los campos de dirección', 'error'); return;
    }
    setGuardandoInfo(true);
    try {
      const san = (s) => (s || '').replace(/<[^>]*>/g, '').replace(/[<>"'`]/g, '').trim().slice(0, 200);
      const updateData = {
        direccion_calle: san(infoCalle), no_exterior: san(infoNoExt), no_interior: san(infoNoInt),
        colonia: san(infoColonia), ciudad: san(infoCiudad), estado: san(infoEstado), codigo_postal: infoCP,
      };
      const { error } = await supabase.from('usuarios').update(updateData).eq('id', usuario.id);
      if (error) { showToast('Error al guardar: ' + error.message, 'error'); setGuardandoInfo(false); return; }
      if (setUsuario) setUsuario(prev => ({ ...prev, ...updateData }));
      setEditandoInfo(false);
      showToast('Dirección guardada correctamente', 'success');
    } catch(e) {
      showToast('Error: ' + e.message, 'error');
    }
    setGuardandoInfo(false);
  };

  const iniciarVerificacion = async () => {
    setVerificandoIdentidad(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch('https://tandamx-bot.vercel.app/api/didit-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
      });
      const data = await resp.json();
      if (!resp.ok) { showToast(data.error || 'Error al iniciar verificación', 'error'); setVerificandoIdentidad(false); return; }
      // Redirigir a Didit para la verificación
      if (setUsuario) setUsuario(prev => ({ ...prev, verificacion_identidad: 'en_proceso' }));
      window.location.href = data.url;
    } catch(e) {
      showToast('Error: ' + e.message, 'error');
    }
    setVerificandoIdentidad(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: C.gray50, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <NavbarApp onMenuOpen={onMenu} titulo="Mi perfil" onHome={onHome} />
      <Toast msg={toast.msg} type={toast.type} />
      <div style={{ padding: '16px 16px 80px' }}>
        {/* Header */}
        <Card style={{ marginBottom: 16, textAlign: 'center', padding: '24px 16px' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, margin: '0 auto 12px' }}>👤</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: C.text, marginBottom: 6 }}>{usuario.nombre}</div>
          <div style={{ marginBottom: 8 }}><NivelBadge nivel={usuario.nivel} /></div>
          <div style={{ fontSize: 12, color: C.textLt, marginBottom: 16 }}>{usuario.email}</div>

          {/* No. Cliente */}
          {usuario.codigo_ref && (
            <div style={{ background: `linear-gradient(135deg, ${C.primaryLt}, #DBEAFE)`, borderRadius: 14, padding: '20px 16px', border: `1.5px solid ${C.primaryMd}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.primary, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Tu No. de Cliente</div>
              <div style={{ fontSize: 32, fontWeight: 900, color: C.primary, letterSpacing: '0.12em', marginBottom: 14, fontFamily: 'monospace', textAlign: 'center' }}>{usuario.codigo_ref}</div>
              <div style={{ fontSize: 12, color: C.textMd, lineHeight: 1.7, marginBottom: 14 }}>
                Este es tu número de referencia para <strong>todos tus movimientos</strong> en TandaClick. Úsalo como concepto en tus transferencias SPEI. Los datos para depositar en OXXO los encuentras dentro de tu tanda. Tenlo siempre a la mano.
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(usuario.codigo_ref); setCopiadoCliente(true); setTimeout(() => setCopiadoCliente(false), 2000); }}
                style={{ width: '100%', padding: '12px 20px', borderRadius: 10, border: 'none', background: copiadoCliente ? '#059669' : C.primary, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'background 0.2s' }}>
                {copiadoCliente ? '✓ Copiado' : '📋 Copiar No. de Cliente'}
              </button>
            </div>
          )}
        </Card>

        {/* Método de pago y cobro */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.textMd }}>💳 MÉTODO DE PAGO Y COBRO</div>
            <button onClick={() => setEditandoMetodo(true)} style={{ fontSize: 12, fontWeight: 700, color: C.primary, background: C.primaryLt, border: `1px solid ${C.primaryMd}`, borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Cambiar</button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${C.gray100}` }}>
            <div>
              <div style={{ fontSize: 12, color: C.textLt, marginBottom: 2 }}>Cómo pagas tu tanda</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>
                {usuario.metodo_pago === 'oxxo' ? '🏪 OXXO' : '🏦 Transferencia SPEI'}
              </div>
            </div>
          </div>
          <div style={{ padding: '12px 0' }}>
            <div style={{ fontSize: 12, color: C.textLt, marginBottom: 2 }}>Cómo cobras tu tanda</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>
              {usuario.metodo_cobro === 'oxxo' ? '📱 OXXO con QR' : '🏦 Transferencia SPEI'}
            </div>
            {usuario.metodo_cobro === 'spei' && usuario.clabe && (
              <div style={{ fontSize: 11, color: C.textLt, marginTop: 2, fontFamily: 'monospace' }}>CLABE: ••••{usuario.clabe.slice(-4)} · {usuario.banco || ''} · {usuario.nombre_titular || ''}</div>
            )}
            {usuario.metodo_cobro === 'oxxo' && (
              <div style={{ fontSize: 11, color: C.textLt, marginTop: 2 }}>Te enviaremos un código QR cuando sea tu turno</div>
            )}
          </div>
        </Card>

        {/* Modal editar método de pago */}
        {editandoMetodo && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
            <div style={{ background: C.white, borderRadius: '20px 20px 0 0', padding: '24px 20px 36px', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: C.text, marginBottom: 20 }}>✏️ Cambiar método de pago</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 10 }}>¿Cómo pagas tu tanda?</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                {[{v:'spei',icon:'🏦',label:'Transferencia SPEI'},{v:'oxxo',icon:'🏪',label:'Depósito OXXO'}].map(o => (
                  <button key={o.v} onClick={() => setNuevoPago(o.v)} style={{ padding: '12px 8px', borderRadius: 10, border: `2px solid ${nuevoPago===o.v ? C.primary : C.gray200}`, background: nuevoPago===o.v ? C.primaryLt : C.white, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", textAlign: 'center' }}>
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{o.icon}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: nuevoPago===o.v ? C.primary : C.text }}>{o.label}</div>
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 10 }}>¿Cómo cobras tu tanda?</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                {[{v:'spei',icon:'🏦',label:'Transferencia SPEI'},{v:'oxxo',icon:'📱',label:'Cobrar en OXXO'}].map(o => (
                  <button key={o.v} onClick={() => setNuevoCobro(o.v)} style={{ padding: '12px 8px', borderRadius: 10, border: `2px solid ${nuevoCobro===o.v ? C.primary : C.gray200}`, background: nuevoCobro===o.v ? C.primaryLt : C.white, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", textAlign: 'center' }}>
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{o.icon}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: nuevoCobro===o.v ? C.primary : C.text }}>{o.label}</div>
                  </button>
                ))}
              </div>
              {nuevoCobro === 'spei' && (
                <div style={{ marginBottom: 16 }}>
                  <Input label="CLABE (18 dígitos) *" value={nuevaClabe} onChange={v => setNuevaClabe(v.replace(/\D/g, '').slice(0, 18))} placeholder="18 dígitos" />
                  <Input label="Nombre del titular *" value={nuevoTitular} onChange={setNuevoTitular} placeholder="Como aparece en tu banco" />
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>Banco *</div>
                    <select value={nuevoBanco} onChange={e => setNuevoBanco(e.target.value)}
                      style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${nuevoBanco ? C.primary : C.gray200}`, fontSize: 14, color: nuevoBanco ? C.text : C.textLt, background: C.white, outline: 'none', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      <option value="">— Selecciona tu banco —</option>
                      {['BBVA','Banorte','Santander','HSBC','Scotiabank','Citibanamex','Banco Azteca','BanCoppel','Spin by OXXO','Nu','Rappi','Mercado Pago','Hey Banco','Inbursa','Banregio','Afirme','BanBajio','Otro'].map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
              )}
              {nuevoCobro === 'oxxo' && (
                <div style={{ background: C.primaryLt, border: `1px solid ${C.primaryMd}`, borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.primary, marginBottom: 4 }}>📱 Cobro en OXXO</div>
                  <div style={{ fontSize: 12, color: C.textMd, lineHeight: 1.6 }}>Cuando sea tu turno de cobrar, te enviaremos un código QR a tu WhatsApp para que puedas retirar tu tanda en cualquier OXXO.</div>
                </div>
              )}
              <Btn onClick={guardarMetodo} disabled={guardandoMetodo} style={{ marginBottom: 10 }}>{guardandoMetodo ? 'Guardando...' : 'Guardar cambios'}</Btn>
              <Btn onClick={() => setEditandoMetodo(false)} variant="ghost">Cancelar</Btn>
            </div>
          </div>
        )}

        {/* Requisitos para ingresar a una tanda */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.textMd, marginBottom: 4 }}>📋 PARA INGRESAR A UNA TANDA</div>
          <div style={{ fontSize: 12, color: C.textLt, marginBottom: 14, lineHeight: 1.5 }}>Completa estos datos para que el organizador pueda aceptarte.</div>

          {/* Dirección */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${C.gray100}` }}>
            <span style={{ fontSize: 20 }}>🗺️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Dirección</div>
              {usuario.direccion_calle
                ? <div style={{ fontSize: 11, color: C.success, marginTop: 1 }}>✓ {usuario.direccion_calle}, {usuario.colonia}</div>
                : <div style={{ fontSize: 11, color: C.warning, marginTop: 1 }}>Pendiente</div>
              }
            </div>
          </div>

          {/* Verificación de identidad (Didit) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${C.gray100}` }}>
            <span style={{ fontSize: 20 }}>🪪</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Verificación de identidad</div>
              <div style={{ fontSize: 11, marginTop: 1, color:
                usuario.verificacion_identidad === 'aprobada' ? C.success :
                usuario.verificacion_identidad === 'rechazada' ? C.danger :
                usuario.verificacion_identidad === 'en_proceso' ? C.warning : C.warning
              }}>
                {usuario.verificacion_identidad === 'aprobada' ? '✓ Identidad verificada' :
                 usuario.verificacion_identidad === 'rechazada' ? '✕ Rechazada — intenta de nuevo' :
                 usuario.verificacion_identidad === 'en_proceso' ? '⏳ En proceso...' :
                 'Pendiente — INE + selfie'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            {!usuario.direccion_calle && (
              <Btn variant="outline" onClick={() => setEditandoInfo(true)} style={{ flex: 1 }}>Completar dirección</Btn>
            )}
            {usuario.verificacion_identidad !== 'aprobada' && (
              <Btn onClick={iniciarVerificacion} disabled={verificandoIdentidad} style={{ flex: 1 }}>
                {verificandoIdentidad ? 'Abriendo...' :
                 usuario.verificacion_identidad === 'rechazada' ? 'Reintentar verificación' :
                 usuario.verificacion_identidad === 'en_proceso' ? 'Continuar verificación' :
                 'Verificar identidad'}
              </Btn>
            )}
            {usuario.direccion_calle && usuario.verificacion_identidad === 'aprobada' && (
              <div style={{ background: C.successLt, borderRadius: 10, padding: '12px 16px', fontSize: 13, color: C.success, fontWeight: 600, textAlign: 'center', width: '100%' }}>
                ✓ Todo listo para unirte a una tanda
              </div>
            )}
          </div>
        </Card>

        {/* Modal completar información */}
        {editandoInfo && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
            <div style={{ background: C.white, borderRadius: '20px 20px 0 0', padding: '24px 20px 36px', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: C.text, marginBottom: 4 }}>🗺️ Completar dirección</div>
              <div style={{ fontSize: 13, color: C.textMd, marginBottom: 20, lineHeight: 1.6 }}>Tu dirección es necesaria para poder unirte a una tanda.</div>

              {/* Código postal con autocompletado */}
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>Código Postal</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={infoCP} onChange={e => setInfoCP(e.target.value)}
                    placeholder="Ej: 64000" maxLength={5}
                    style={{ flex: 1, padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    onFocus={e => e.target.style.borderColor = C.primary}
                    onBlur={e => e.target.style.borderColor = C.gray200}
                  />
                  <button onClick={buscarCP} disabled={buscandoCP || infoCP.length < 5} style={{ padding: '11px 16px', borderRadius: 10, border: 'none', background: buscandoCP ? C.gray100 : C.primary, color: C.white, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: 'nowrap' }}>
                    {buscandoCP ? '...' : 'Buscar'}
                  </button>
                </div>
              </div>

              {/* Estado y Ciudad — autollenados */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>Estado</div>
                  <input value={infoEstado} onChange={e => setInfoEstado(e.target.value)} placeholder="Autollenado" style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 13, color: C.text, background: infoEstado ? C.primaryLt : C.white, outline: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif", boxSizing: 'border-box' }} readOnly={!!infoEstado} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>Ciudad / Municipio</div>
                  <input value={infoCiudad} onChange={e => setInfoCiudad(e.target.value)} placeholder="Autollenado" style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 13, color: C.text, background: infoCiudad ? C.primaryLt : C.white, outline: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif", boxSizing: 'border-box' }} readOnly={!!infoCiudad} />
                </div>
              </div>

              {/* Colonia — dropdown si hay opciones */}
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>Colonia</div>
                {coloniaOpciones.length > 0 ? (
                  <select value={infoColonia} onChange={e => setInfoColonia(e.target.value)} style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.primary}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    <option value="">— Selecciona tu colonia —</option>
                    {coloniaOpciones.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                ) : (
                  <input value={infoColonia} onChange={e => setInfoColonia(e.target.value)} placeholder="Ej: Centro" style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif" }} />
                )}
              </div>

              {/* Calle, No. Ext, No. Int */}
              <Input label="Calle" value={infoCalle} onChange={setInfoCalle} placeholder="Ej: Av. Reforma" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>No. Exterior</div>
                  <input value={infoNoExt} onChange={e => setInfoNoExt(e.target.value)} placeholder="123" style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif", boxSizing: 'border-box' }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.textMd, marginBottom: 6 }}>No. Interior <span style={{ fontWeight: 400, color: C.textLt }}>(opcional)</span></div>
                  <input value={infoNoInt} onChange={e => setInfoNoInt(e.target.value)} placeholder="A, 2B..." style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`, fontSize: 14, color: C.text, background: C.white, outline: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif", boxSizing: 'border-box' }} />
                </div>
              </div>

              <Btn onClick={guardarInfo} disabled={guardandoInfo} style={{ marginBottom: 10 }}>{guardandoInfo ? 'Guardando...' : 'Guardar dirección'}</Btn>
              <Btn onClick={() => setEditandoInfo(false)} variant="ghost">Cancelar</Btn>
            </div>
          </div>
        )}

        {/* Biométrico — Próximamente */}
        <Card style={{ marginBottom: 16, opacity: 0.5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.textMd }}>🔐 ACCESO BIOMÉTRICO</div>
            <span style={{ background: C.warningLt, color: C.warning, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99 }}>Próximamente</span>
          </div>
          <div style={{ fontSize: 13, color: C.textLt, marginTop: 8 }}>Face ID / Huella digital para acceder sin contraseña.</div>
        </Card>


      </div>
    </div>
  );
}

export { MiPerfil };
